import {
  AlignLeft,
  ArrowLeft,
  AudioLines,
  ArrowUp,
  ArrowDown01,
  ArrowDownAZ,
  ArrowUp10,
  ArrowUpRight,
  ArrowUpZA,
  Ban,
  Bell,
  Bold,
  Bookmark,
  BookmarkMinus,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Circle,
  CircleDot,
  Copy,
  ClockFading,
  CreditCard,
  File,
  FileText,
  ExternalLink,
  Filter,
  Gift,
  Globe,
  GraduationCap,
  GripVertical,
  Heart,
  Info,
  Italic,
  Languages,
  LayoutDashboard,
  LayoutList,
  LayoutTemplate,
  List,
  ListChecks,
  ListOrdered,
  Loader,
  Lock,
  MapPin,
  type LucideIcon,
  Maximize2,
  Megaphone,
  Mic,
  MicOff,
  Minimize2,
  MessageCircle,
  MessageCircleOff,
  MessageCirclePlus,
  MoreHorizontal,
  Download,
  Paperclip,
  Pause,
  Pencil,
  PenLine,
  Play,
  PanelLeft,
  PanelRight,
  PanelRightClose,
  Plus,
  Reply,
  Search,
  Settings,
  ShoppingCart,
  Sparkles,
  Square,
  SquareCheck,
  Star,
  Strikethrough,
  Trash2,
  Users,
  Video,
  Wallet,
  X,
  Zap,
  Flag,
  ChevronUp,
} from 'lucide-react';

import {
  MaterialBusinessCenter,
  MaterialEdit,
  MaterialMoney,
  MaterialPlace,
  MaterialVerified,
} from './material.js';

interface IconProps {
  name: IconName;
  size?: number;
  /** Override the icon's default fill (e.g. outline heart vs filled heart). */
  filled?: boolean;
}

export type IconName =
  | 'dashboard'
  | 'chat'
  | 'briefcase'
  | 'money'
  | 'businessCenter'
  | 'place'
  | 'verified'
  | 'search'
  | 'building'
  | 'star'
  | 'cart'
  | 'card'
  | 'megaphone'
  | 'gear'
  | 'users'
  | 'bell'
  | 'gift'
  | 'grip'
  | 'panel'
  | 'panelRight'
  | 'panelRightClose'
  | 'plus'
  | 'close'
  | 'file'
  | 'fileText'
  | 'paperclip'
  | 'download'
  | 'template'
  | 'post'
  | 'back'
  | 'filter'
  | 'layoutList'
  | 'pencil'
  | 'edit'
  | 'trash'
  | 'check'
  | 'checkCheck'
  | 'bookmark'
  | 'bookmarkMinus'
  | 'chatBubble'
  | 'chevronRight'
  | 'chevronLeft'
  | 'calendar'
  | 'video'
  | 'mapPin'
  | 'languages'
  | 'flag'
  | 'sparkles'
  | 'wallet'
  | 'globe'
  | 'graduationCap'
  | 'chevronDown'
  | 'clockFading'
  | 'spinner'
  | 'messageCirclePlus'
  | 'arrowUp'
  | 'arrowUpRight'
  | 'alignLeft'
  | 'penLine'
  | 'arrowDown01'
  | 'arrowUp10'
  | 'arrowDownAZ'
  | 'arrowUpZA'
  | 'bolt'
  | 'expand'
  | 'chevronUp'
  | 'chevronsUpDown'
  | 'chevronsDownUp'
  | 'chevronsLeft'
  | 'chevronsRight'
  | 'more'
  | 'ban'
  | 'heart'
  | 'chatOff'
  | 'info'
  | 'lock'
  | 'reply'
  | 'externalLink'
  | 'bold'
  | 'italic'
  | 'strikethrough'
  | 'list'
  | 'listChecks'
  | 'listOrdered'
  | 'collapse'
  | 'copy'
  | 'mic'
  | 'audioLines'
  | 'micOff'
  | 'stop'
  | 'play'
  | 'pause'
  // The form vocabulary: the card's head glyph, and the radio or the box —
  // empty or filled — that a question's options are drawn with.
  | 'radio'
  | 'radioOn'
  | 'tickbox'
  | 'tickboxOn';

const ICONS: Record<IconName, LucideIcon> = {
  money: MaterialMoney,
  businessCenter: MaterialBusinessCenter,
  place: MaterialPlace,
  verified: MaterialVerified,
  edit: MaterialEdit,
  dashboard: LayoutDashboard,
  chat: MessageCircle,
  briefcase: Briefcase,
  search: Search,
  building: Building2,
  star: Star,
  cart: ShoppingCart,
  card: CreditCard,
  megaphone: Megaphone,
  gear: Settings,
  users: Users,
  bell: Bell,
  gift: Gift,
  grip: GripVertical,
  panel: PanelLeft,
  panelRight: PanelRight,
  panelRightClose: PanelRightClose,
  plus: Plus,
  close: X,
  file: File,
  fileText: FileText,
  // Material Symbols `attach_file` in @ajt/stream-chat; paperclip is lucide's
  // equivalent, so an attachment reads the same in both products.
  paperclip: Paperclip,
  download: Download,
  template: LayoutTemplate,
  post: Plus,
  back: ArrowLeft,
  filter: Filter,
  layoutList: LayoutList,
  pencil: Pencil,
  trash: Trash2,
  check: Check,
  checkCheck: CheckCheck,
  bookmark: Bookmark,
  bookmarkMinus: BookmarkMinus,
  chatBubble: MessageCircle,
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  calendar: Calendar,
  video: Video,
  mapPin: MapPin,
  // The four candidate rows of the filter dialog, in its rail order.
  languages: Languages,
  flag: Flag,
  sparkles: Sparkles,
  wallet: Wallet,
  globe: Globe,
  graduationCap: GraduationCap,
  chevronDown: ChevronDown,
  clockFading: ClockFading,
  /*
   * The segmented ring, not `LoaderCircle`'s arc. It turns beside a phrase that
   * already says what is happening, so its whole job is to say that the phrase
   * is still current — a still glyph next to "Reading the conversation…" leaves
   * the employer unable to tell a slow read from a stalled one. Twelve ticks
   * read as motion at 15px where an arc reads as a comma.
   */
  spinner: Loader,
  messageCirclePlus: MessageCirclePlus,
  arrowUp: ArrowUp,
  arrowUpRight: ArrowUpRight,
  alignLeft: AlignLeft,
  penLine: PenLine,
  arrowDown01: ArrowDown01,
  arrowUp10: ArrowUp10,
  arrowDownAZ: ArrowDownAZ,
  arrowUpZA: ArrowUpZA,
  bolt: Zap,
  expand: Maximize2,
  chevronUp: ChevronUp,
  // The mobile fold's own pair: apart to open, together to close.
  chevronsUpDown: ChevronsUpDown,
  chevronsDownUp: ChevronsDownUp,
  chevronsLeft: ChevronsLeft,
  chevronsRight: ChevronsRight,
  more: MoreHorizontal,
  ban: Ban,
  heart: Heart,
  chatOff: MessageCircleOff,
  info: Info,
  lock: Lock,
  reply: Reply,
  externalLink: ExternalLink,
  bold: Bold,
  italic: Italic,
  strikethrough: Strikethrough,
  list: List,
  listChecks: ListChecks,
  listOrdered: ListOrdered,
  // The unpicked marks stay empty shapes; the picked pair carries the dot/tick.
  radio: Circle,
  radioOn: CircleDot,
  tickbox: Square,
  tickboxOn: SquareCheck,
  collapse: Minimize2,
  copy: Copy,
  mic: Mic,
  audioLines: AudioLines,
  micOff: MicOff,
  stop: Square,
  play: Play,
  pause: Pause,
};

/* Glyphs whose resting form is filled. A stage glyph never belongs here: a
   filled stage glyph means the stage is set, which only the caller knows. */
const FILLED: Partial<Record<IconName, boolean>> = {
  dashboard: true,
  chatBubble: true,
  stop: true,
  play: true,
  pause: true,
};

export function Icon({ name, size = 18, filled }: IconProps) {
  const LucideGlyph = ICONS[name];
  const isFilled = filled ?? FILLED[name] ?? false;
  return (
    <LucideGlyph
      size={size}
      strokeWidth={1.8}
      fill={isFilled ? 'currentColor' : 'none'}
      aria-hidden="true"
    />
  );
}

/**
 * Material Symbols "waving_hand" (filled) — a friendly wave that lucide lacks.
 * Standalone because it uses Material's fill-based path + viewBox, not lucide's
 * stroke system. Source: Google Material Symbols (Apache-2.0).
 */
export function WavingHandIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
      <path d="M39-680q0-100 70.5-170.5T280-921v81q-66 0-113 47t-47 113H39Zm173 469q-91-91-91-219t91-219l70-71 12 12q29 29 29 70.5T294-567l-14 14q-12 12-12 28.5t12 28.5l36 36q26 26 26 63t-26 63l43 43q44-44 44-105.5T358-503l-22-22q26-26 37-58.5t9-66.5l179-179q12-12 28.5-12t28.5 12q12 12 12 28.5T618-772L431-585l42 42 241-240q12-12 28-12t28 12q12 12 12 28t-12 28L530-486l42 42 212-212q12-12 28.5-12t28.5 12q12 12 12 28.5T841-599L629-387l42 42 162-162q12-12 28.5-12t28.5 12q12 12 12 28.5T890-450L650-211q-91 91-219 91t-219-91ZM680-39v-81q66 0 113-47t47-113h81q0 100-70.5 170.5T680-39Z" />
    </svg>
  );
}
